
'use client';

import { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import type { Friend, UpsertFriend } from '@/types';
import { db, storage } from '@/lib/firebase';
import { collection, onSnapshot, query, orderBy, Timestamp, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useToast } from '@/hooks/use-toast';
import { v4 as uuidv4 } from 'uuid';

interface FriendContextType {
    friends: Friend[];
    loading: boolean;
    getFriend: (id: string) => Promise<Friend | null>;
    createFriend: (friend: UpsertFriend) => Promise<void>;
    updateFriend: (friendId: string, friend: UpsertFriend) => Promise<void>;
    deleteFriend: (friendId: string) => Promise<void>;
}

const FriendContext = createContext<FriendContextType | undefined>(undefined);

const safeGetDate = (timestamp: any): Date => {
    if (timestamp instanceof Timestamp) return timestamp.toDate();
    if (timestamp instanceof Date) return timestamp;
    return new Date();
};

export const FriendProvider = ({ children }: { children: ReactNode }) => {
    const [friends, setFriends] = useState<Friend[]>([]);
    const [loading, setLoading] = useState(true);
    const { toast } = useToast();

    useEffect(() => {
        const q = query(collection(db, "friends"), orderBy("createdAt", "desc"));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const friendsData: Friend[] = [];
            querySnapshot.forEach((doc) => {
                const data = doc.data();
                friendsData.push({
                    id: doc.id,
                    name: data.name,
                    avatarUrl: data.avatarUrl,
                    avatarPath: data.avatarPath,
                    bio: data.bio,
                    socials: data.socials || {},
                    tags: data.tags || [],
                    createdAt: safeGetDate(data.createdAt),
                });
            });
            setFriends(friendsData);
            setLoading(false);
        }, (error) => {
            console.error("Firestore snapshot error (friends):", error);
            toast({
                title: "Connection Error",
                description: "Could not load friend list.",
                variant: "destructive",
            });
            setLoading(false);
        });

        return () => unsubscribe();
    }, [toast]);

    const getFriend = useCallback(async (id: string): Promise<Friend | null> => {
        setLoading(true);
        try {
            const docRef = doc(db, 'friends', id);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                const data = docSnap.data();
                return {
                     id: docSnap.id,
                     name: data.name,
                     avatarUrl: data.avatarUrl,
                     avatarPath: data.avatarPath,
                     bio: data.bio,
                     socials: data.socials || {},
                     tags: data.tags || [],
                     createdAt: safeGetDate(data.createdAt),
                }
            }
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const createFriend = async (friend: UpsertFriend) => {
        if (!friend.avatarFile) {
            toast({ title: "Image required", description: "Please provide a profile picture.", variant: "destructive" });
            return;
        }

        try {
            const avatarPath = `avatars/friends/${uuidv4()}`;
            const storageRef = ref(storage, avatarPath);
            await uploadBytes(storageRef, friend.avatarFile);
            const avatarUrl = await getDownloadURL(storageRef);

            const friendToCreate = {
                 name: friend.name,
                 bio: friend.bio,
                 socials: friend.socials,
                 tags: friend.tags,
                 avatarUrl,
                 avatarPath,
                 createdAt: serverTimestamp(),
            }

            await addDoc(collection(db, 'friends'), friendToCreate);
            toast({ title: "Success", description: `${friend.name} has been added.` });
        } catch (error) {
            console.error("Error creating friend:", error);
            toast({ title: "Error", description: "Creation failed.", variant: "destructive" });
        }
    };
    
    const updateFriend = async (friendId: string, friendData: UpsertFriend) => {
        try {
            const friendRef = doc(db, 'friends', friendId);
            const updatePayload: any = {
                name: friendData.name,
                bio: friendData.bio,
                socials: friendData.socials,
                tags: friendData.tags,
            };

            if (friendData.avatarFile) {
                 const avatarPath = `avatars/friends/${uuidv4()}`;
                 const storageRef = ref(storage, avatarPath);
                 await uploadBytes(storageRef, friendData.avatarFile);
                 const avatarUrl = await getDownloadURL(storageRef);
                 updatePayload.avatarUrl = avatarUrl;
                 updatePayload.avatarPath = avatarPath;
            }

            await updateDoc(friendRef, updatePayload);
            toast({ title: "Success", description: "Profile updated." });

        } catch (error) {
             console.error("Error updating friend:", error);
             toast({ title: "Error", description: "Update failed.", variant: "destructive" });
        }
    };

    const deleteFriend = async (friendId: string) => {
        try {
            const friendToDelete = friends.find(f => f.id === friendId) || await getFriend(friendId);
            if(friendToDelete && friendToDelete.avatarPath) {
                await deleteObject(ref(storage, friendToDelete.avatarPath));
            }
            await deleteDoc(doc(db, 'friends', friendId));
            toast({ title: "Success", description: "Friend deleted." });
        } catch(error) {
            console.error("Error deleting friend:", error);
            toast({ title: "Error", description: "Deletion failed.", variant: "destructive" });
        }
    };

    return (
        <FriendContext.Provider value={{ friends, loading, getFriend, createFriend, updateFriend, deleteFriend }}>
            {children}
        </FriendContext.Provider>
    );
};

export const useFriend = () => {
    const context = useContext(FriendContext);
    if (context === undefined) {
        throw new Error('useFriend must be used within a FriendProvider');
    }
    return context;
};
